import {
  AlarmClock,
  CircleAlert,
  CircleCheck,
  CircleX,
  Eye,
  EyeOff,
  FlaskConical,
  LoaderCircle,
  TriangleAlert,
} from 'lucide-react';
import { ComponentType } from 'react';
import type { StatusPillTone } from '../components/portal/StatusPill';
import { cn } from '../lib/utils';
import {
  LiveVersionComparison,
  ReleaseStatus,
  SubmissionStatus,
} from '../types/api';

export type { ReleaseStatus, SubmissionStatus };

export type CombinedStatus =
  | 'InReview'
  | 'Live'
  | 'WaitingToGoLive'
  | 'Deprecated'
  | 'Suspended'
  | 'Rejected'
  | 'Processing'
  | 'Processed'
  | 'ProcessingFailed'
  | 'AlphaTesting'
  | 'ActionNeeded'
  | 'Error';

interface StatusConfig {
  label: string;
  icon: ComponentType<{ className?: string }>;
  iconClassName: string;
  textColorClassName: string;
  /** Tone for the pill rendering of the same status (Overview / detail header). */
  pillTone: StatusPillTone;
}

/** 16px box holding the pulsing "Live" dot, shaped like the lucide icons. */
function LiveDotIcon({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex size-4 shrink-0 items-center justify-center',
        className,
      )}
    >
      <span className="size-1.5 rounded-full bg-current animate-live-pulse" />
    </span>
  );
}

const combinedStatusConfig: Record<CombinedStatus, StatusConfig> = {
  InReview: {
    label: 'In Review',
    icon: Eye,
    iconClassName: 'size-4 text-amber-400',
    textColorClassName: 'text-amber-400',
    pillTone: 'review',
  },
  Live: {
    label: 'Live',
    icon: LiveDotIcon,
    iconClassName: 'text-green-400',
    textColorClassName: 'text-green-400',
    pillTone: 'live',
  },
  WaitingToGoLive: {
    label: 'Waiting to go live',
    icon: AlarmClock,
    iconClassName: 'size-4 text-blue-400',
    textColorClassName: 'text-blue-400',
    pillTone: 'waiting',
  },
  Deprecated: {
    label: 'Deprecated',
    icon: EyeOff,
    iconClassName: 'size-4 text-seeker-400',
    textColorClassName: 'text-seeker-400',
    pillTone: 'neutral',
  },
  Suspended: {
    label: 'Suspended',
    icon: CircleAlert,
    iconClassName: 'size-4 text-orange-400',
    textColorClassName: 'text-orange-400',
    pillTone: 'suspended',
  },
  Rejected: {
    label: 'Rejected',
    icon: CircleX,
    iconClassName: 'size-4 text-rose-400',
    textColorClassName: 'text-rose-400',
    pillTone: 'rejected',
  },
  Processing: {
    label: 'Processing',
    icon: LoaderCircle,
    iconClassName: 'size-4 text-seeker-300 animate-spin',
    textColorClassName: 'text-seeker-300',
    pillTone: 'processing',
  },
  Processed: {
    label: 'Processed',
    icon: CircleCheck,
    iconClassName: 'size-4 text-white',
    textColorClassName: 'text-white',
    pillTone: 'default',
  },
  ProcessingFailed: {
    label: 'Processing Failed',
    icon: CircleX,
    iconClassName: 'size-4 text-rose-400',
    textColorClassName: 'text-rose-400',
    pillTone: 'rejected',
  },
  AlphaTesting: {
    label: 'Alpha Testing',
    icon: FlaskConical,
    iconClassName: 'size-4 text-purple-400',
    textColorClassName: 'text-purple-400',
    pillTone: 'default',
  },
  ActionNeeded: {
    label: 'Action Needed',
    icon: TriangleAlert,
    iconClassName: 'size-4 text-amber-400',
    textColorClassName: 'text-amber-400',
    pillTone: 'review',
  },
  Error: {
    label: 'Error',
    icon: CircleAlert,
    iconClassName: 'size-4 text-red-500',
    textColorClassName: 'text-red-500',
    pillTone: 'rejected',
  },
};

export interface ReleaseStatusOverrides {
  isLive?: boolean;
  liveVersionComparison?: LiveVersionComparison;
  hubspotTicketId?: string | null;
  hubspotTicketClosedDate?: string | null;
  nftMintAddress?: string | null;
  isSupersededByNewerSubmission?: boolean;
  /**
   * The release the Store would serve, but the app is still unpublished there
   * (see `isReleaseWaitingToGoLive`). Only resolvable by staff who can read
   * the Store publication state.
   */
  isWaitingToGoLive?: boolean;
}

function resolveCombinedStatus(
  releaseStatus: ReleaseStatus,
  submissionStatus: SubmissionStatus,
  overrides: ReleaseStatusOverrides,
): CombinedStatus {
  const {
    isLive,
    liveVersionComparison,
    hubspotTicketId,
    hubspotTicketClosedDate,
    nftMintAddress,
    isSupersededByNewerSubmission,
    isWaitingToGoLive,
  } = overrides;
  const isHubspotTicketOpen =
    Boolean(hubspotTicketId) && hubspotTicketClosedDate == null;
  const isHubspotTicketClosed =
    Boolean(hubspotTicketId) && hubspotTicketClosedDate != null;

  if (isWaitingToGoLive) {
    return 'WaitingToGoLive';
  }

  if (isLive === true) {
    return 'Live';
  }

  if (isSupersededByNewerSubmission) {
    return 'Deprecated';
  }

  if (liveVersionComparison === 'Older') {
    return 'Deprecated';
  }

  if (
    releaseStatus === 'Processed' &&
    submissionStatus === 'NotSubmitted' &&
    (!nftMintAddress || !hubspotTicketId)
  ) {
    return 'ActionNeeded';
  }

  if (releaseStatus === 'Published') {
    return 'Live';
  }

  if (submissionStatus === 'AlphaTest') {
    return 'AlphaTesting';
  }

  if (submissionStatus === 'Submitted' || submissionStatus === 'UnderReview') {
    return releaseStatus === 'Processed' && isHubspotTicketClosed
      ? 'Processed'
      : 'InReview';
  }

  if (submissionStatus === 'Approved') {
    return isHubspotTicketOpen ? 'InReview' : 'Processed';
  }

  if (submissionStatus === 'ProcessingFailed') {
    return 'Error';
  }

  if (submissionStatus === 'RequiresChanges') {
    return 'Suspended';
  }

  if (submissionStatus === 'Rejected' || releaseStatus === 'Rejected') {
    return 'Rejected';
  }

  if (releaseStatus === 'Archived') {
    return 'Deprecated';
  }

  if (releaseStatus === 'Processing') {
    return 'Processing';
  }

  if (releaseStatus === 'ProcessingFailed') {
    return 'ProcessingFailed';
  }

  return 'Processed';
}

export function getStatusDisplay(
  releaseStatus: ReleaseStatus,
  submissionStatus: SubmissionStatus,
  overrides: ReleaseStatusOverrides = {},
): StatusConfig {
  const combinedStatus = resolveCombinedStatus(
    releaseStatus,
    submissionStatus,
    overrides,
  );
  return combinedStatusConfig[combinedStatus];
}

interface ReleaseStatusSource {
  status: ReleaseStatus;
  submissionStatus: SubmissionStatus;
  versionCode?: number | null;
  isLive?: boolean;
  liveVersionComparison?: LiveVersionComparison;
  hubspotTicketId?: string | null;
  hubspotTicketClosedDate?: string | null;
  nftMintAddress?: string | null;
  isSupersededByNewerSubmission?: boolean;
}

interface ReleaseStatusContext {
  /** `Dapp.publishedVersionCode`: the release the Store would serve. */
  publishedVersionCode?: number | null;
  /** Store publication state; only known to staff (`useDappStoreStatus`). */
  storeStatus?: 'published' | 'unpublished' | null;
}

/**
 * A release is "waiting to go live" when it is the Store's current version but
 * the app itself is still unpublished on the Store — the "Go Live Now" case.
 * The Store publication state wins over the API's `isLive` flag.
 */
export function isReleaseWaitingToGoLive(
  release: Pick<ReleaseStatusSource, 'versionCode'>,
  context: ReleaseStatusContext,
): boolean {
  return (
    context.storeStatus === 'unpublished' &&
    release.versionCode != null &&
    release.versionCode === context.publishedVersionCode
  );
}

/** One-call status resolution for a Release DTO (+ optional Store context). */
export function getReleaseStatusDisplay(
  release: ReleaseStatusSource,
  context: ReleaseStatusContext = {},
): StatusConfig {
  return getStatusDisplay(release.status, release.submissionStatus, {
    isLive: release.isLive,
    liveVersionComparison: release.liveVersionComparison,
    hubspotTicketId: release.hubspotTicketId,
    hubspotTicketClosedDate: release.hubspotTicketClosedDate,
    nftMintAddress: release.nftMintAddress,
    isSupersededByNewerSubmission: release.isSupersededByNewerSubmission,
    isWaitingToGoLive: isReleaseWaitingToGoLive(release, context),
  });
}
